import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Sparkles, Upload, Users, Building2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AdminImport = () => {
  const [creatorFile, setCreatorFile] = useState(null);
  const [brandFile, setBrandFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleCreatorUpload = async () => {
    if (!creatorFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', creatorFile);

      const response = await axios.post(`${API}/admin/import/creators`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult({
        type: 'success',
        message: response.data.message,
        errors: response.data.errors
      });
      toast.success(response.data.message);
      setCreatorFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
      setResult({
        type: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleBrandUpload = async () => {
    if (!brandFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', brandFile);

      const response = await axios.post(`${API}/admin/import/brands`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResult({
        type: 'success',
        message: response.data.message,
        errors: response.data.errors
      });
      toast.success(response.data.message);
      setBrandFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
      setResult({
        type: 'error',
        message: error.response?.data?.detail || 'Upload failed'
      });
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <Link to="/admin">
            <Button variant="ghost">Back to Admin</Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{fontFamily: 'Manrope'}} data-testid="import-heading">
            CSV Import
          </h1>
          <p className="text-lg text-slate-600">Import creators and brands in bulk</p>
        </div>

        {result && (
          <Card className={`p-6 mb-8 ${
            result.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`} data-testid="import-result">
            <div className="flex items-start gap-3">
              {result.type === 'success' ? (
                <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-semibold mb-1 ${
                  result.type === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>{result.message}</p>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-red-900 mb-1">Errors:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      {result.errors.map((error, i) => (
                        <li key={i}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Creator Import */}
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>Import Creators</h2>
                <p className="text-sm text-slate-600">Upload creator data</p>
              </div>
            </div>

            <div className="mb-6">
              <Label className="mb-2 block">CSV Format</Label>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700">
                <p className="font-medium mb-2">Required columns:</p>
                <ul className="space-y-1 ml-4">
                  <li>• channel_name</li>
                  <li>• channel_url</li>
                  <li>• subscriber_count</li>
                  <li>• niche</li>
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="creator-file">Select CSV File</Label>
              <input
                id="creator-file"
                type="file"
                accept=".csv"
                onChange={(e) => setCreatorFile(e.target.files[0])}
                className="mt-2 w-full"
                data-testid="creator-file-input"
              />
              {creatorFile && (
                <p className="text-sm text-slate-600 mt-2">Selected: {creatorFile.name}</p>
              )}
            </div>

            <Button
              onClick={handleCreatorUpload}
              disabled={!creatorFile || uploading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-12"
              data-testid="upload-creators-btn"
            >
              {uploading ? 'Uploading...' : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Creators
                </>
              )}
            </Button>
          </Card>

          {/* Brand Import */}
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>Import Brands</h2>
                <p className="text-sm text-slate-600">Upload brand data</p>
              </div>
            </div>

            <div className="mb-6">
              <Label className="mb-2 block">CSV Format</Label>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700">
                <p className="font-medium mb-2">Required columns:</p>
                <ul className="space-y-1 ml-4">
                  <li>• brand_name</li>
                  <li>• website</li>
                  <li>• category</li>
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="brand-file">Select CSV File</Label>
              <input
                id="brand-file"
                type="file"
                accept=".csv"
                onChange={(e) => setBrandFile(e.target.files[0])}
                className="mt-2 w-full"
                data-testid="brand-file-input"
              />
              {brandFile && (
                <p className="text-sm text-slate-600 mt-2">Selected: {brandFile.name}</p>
              )}
            </div>

            <Button
              onClick={handleBrandUpload}
              disabled={!brandFile || uploading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
              data-testid="upload-brands-btn"
            >
              {uploading ? 'Uploading...' : (
                <>
                  <Upload className="w-5 h-5 mr-2" />
                  Upload Brands
                </>
              )}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminImport;
